import { ReactNode } from "react";
import { Alert, Button, Input } from "reactstrap";
import { SelectPicker } from "rsuite";

import AppSpinner from "../../../elements/AppSpinner";
import CopyGroup from "../../Modals/CopyGroup";
import RequiredTitle from "../../../elements/RequiredTitle";
import { SharedRecordsObject } from "../../../types/GlobalInterfaces";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

interface ActiveSharedRecordI {
  existingSharedRecord: (SharedRecordsObject & { _id: string }) | null;
  sharedRecordObject: SharedRecordsObject;
  expired: boolean;
  SyncToggle: ReactNode;
  ReadOnlyToggle: ReactNode;
  ShareSettings: ReactNode;
  ShareStats: ReactNode;
  handleUnshareRecord: () => void;
  handleShareRecord: () => void;
  loadingState: {
    updatingExpiration: boolean;
    sharing: boolean;
  };
  onChange: (payload: Record<string, any>) => void;
  success: string;
  error: string;
}

const authOptions = [
  { label: "Password Protected", value: "Password" },
  { label: "Require Logged In User", value: "GC User" },
  { label: "None", value: "None" },
];

const SharedRecordTab = ({
  existingSharedRecord,
  sharedRecordObject,
  expired,
  SyncToggle,
  ReadOnlyToggle,
  ShareSettings,
  ShareStats,
  handleUnshareRecord,
  handleShareRecord,
  loadingState,
  error,
  success,
  onChange,
}: ActiveSharedRecordI) => {
  const { updatingExpiration, sharing } = loadingState;

  const authentication = (
    <div className="pt-4">
      <RequiredTitle title="Record Authentication" size="md" />
      <SelectPicker
        data={authOptions}
        value={sharedRecordObject?.Authentication}
        block={true}
        placeholder="Select Record Authentication"
        searchable={false}
        cleanable={false}
        onChange={(val) => onChange({ Authentication: val })}
      />
      {sharedRecordObject?.Authentication === "Password" && (
        <>
          <RequiredTitle title="Password" className="mt-4" size="md" />
          <Input
            bsSize="sm"
            value={sharedRecordObject?.Password ?? ""}
            onChange={(e) => onChange({ Password: e.target.value })}
            placeholder="Password"
          />
        </>
      )}
    </div>
  );

  // Shared Record exists and have Active share link.
  if (existingSharedRecord && !expired) {
    const link = `${window.location.origin}/share/${existingSharedRecord?._id}`;

    return (
      <>
        {authentication}
        <div className="px-0">
          <CopyGroup
            name="Share Link"
            description="Copy and paste this link to share the record."
            link={link}
          />
        </div>
        <h3>
          Expires:{" "}
          {dayjs(existingSharedRecord.Expires).format("MMMM Do YYYY, h:mm a")}
        </h3>
        <div className="my-3">
          <Button size="sm" onClick={handleUnshareRecord}>
            {updatingExpiration ? (
              <AppSpinner color="primary" />
            ) : (
              "Unshare Record"
            )}
          </Button>
        </div>
        {success && (
          <Alert className="p-2 d-flex align-items-center">
            <i className="mdi mdi-check-circle text-lg ml-1 mr-3" />
            {success}
          </Alert>
        )}
        {ShareSettings}
        {ShareStats}
      </>
    );
  }

  return (
    <>
      {authentication}
      {/* Shared record exists, but share link has expired. */}
      {existingSharedRecord && expired && (
        <>
          <h4 className="mb-0 mt-3">Share Link Expired</h4>
          <small>
            This shared record's link expired on{" "}
            {dayjs(existingSharedRecord.Expires).format("MMMM Do YYYY, h:mm a")}
          </small>
        </>
      )}
      <RequiredTitle
        className="mt-4"
        titleCSS="mb-0"
        title="Expires In:"
        size="md"
      />
      <small>How long until the share link for this record will expire.</small>
      <SelectPicker
        className="mt-2 mb-4"
        searchable={false}
        block
        placeholder="Select Expiration"
        value={sharedRecordObject.Expires}
        data={[
          { label: "In 30 minutes", value: "30" },
          { label: "In 1 hour", value: "60" },
          { label: "In 8 hours", value: "480" },
          { label: "In 1 day", value: "1440" },
          { label: "In 1 week", value: "10080" },
          { label: "In 2 weeks", value: "20160" },
        ]}
        onChange={(newExpires) => onChange({ Expires: newExpires || "" })}
      />
      {!existingSharedRecord && SyncToggle}
      {!existingSharedRecord && ReadOnlyToggle}
      <Alert color="warning" className="p-2 mt-4">
        This record's data will accessible by any GrowthCloud user that has the
        link. Ensure you only share this link with those you trust.
      </Alert>
      <div className="d-flex justify-content-end">
        <Button
          color="primary"
          onClick={handleShareRecord}
          disabled={
            !sharedRecordObject.Expires ||
            sharing ||
            updatingExpiration ||
            (sharedRecordObject?.Authentication === "Password" &&
              !sharedRecordObject?.Password)
          }
          style={{ width: 120 }}
        >
          {sharing || updatingExpiration ? (
            <AppSpinner color="white" />
          ) : expired ? (
            "Reshare"
          ) : (
            "Share"
          )}
        </Button>
      </div>
      {error && (
        <Alert color="danger" className="mt-2">
          {error}
        </Alert>
      )}
      {existingSharedRecord && ShareSettings}
      {existingSharedRecord && ShareStats}
    </>
  );
};
export default SharedRecordTab;
